const initializedHeaders = new WeakSet();

export const getFdHeaderMode = (width) => {
  if (width > 1200) {
    return 'desktop';
  }

  return width > 851 ? 'tablet' : 'mobile';
};

export const getNextFdAccordionGroup = (activeGroup, requestedGroup) =>
  activeGroup === requestedGroup ? null : requestedGroup;

export const isFdHeaderCloseKey = (key) => key === 'Escape';

export const isFdHeaderFocusable = (item) =>
  !item.hidden && !item.closest('[inert]') && item.getClientRects().length > 0;

const getFocusableElements = (element) =>
  Array.from(
    element.querySelectorAll(
      'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'
    )
  ).filter(isFdHeaderFocusable);

export const initFdHeader = (scope = document) => {
  const headers = scope.querySelectorAll('[data-fd-header]');

  headers.forEach((header) => {
    if (initializedHeaders.has(header)) {
      return;
    }

    initializedHeaders.add(header);

    const desktopNav = header.querySelector('[data-fd-desktop-nav]');
    const compactNav = header.querySelector('[data-fd-compact-nav]');
    const menuToggle = header.querySelector('[data-fd-menu-toggle]');
    const overlay = header.parentElement?.querySelector('[data-fd-menu-overlay]');
    const desktopTriggers = Array.from(
      header.querySelectorAll('[data-fd-desktop-trigger]')
    );
    const desktopLinks = Array.from(
      desktopNav.querySelectorAll('.fd-header-nav__link')
    );
    const desktopPanels = Array.from(
      header.querySelectorAll('[data-fd-desktop-panel]')
    );
    const compactTriggers = Array.from(
      header.querySelectorAll('[data-fd-compact-trigger]')
    );
    const compactPanels = Array.from(
      header.querySelectorAll('[data-fd-compact-panel]')
    );
    const compactScroll = header.querySelector('[data-fd-compact-scroll]');

    if (!desktopNav || !compactNav || !menuToggle) {
      return;
    }

    let desktopCloseTimeout = null;
    const desktopPanelCloseTimers = new WeakMap();
    const desktopPanelAnimationFrames = new WeakMap();
    const desktopPanelReadyTimers = new WeakMap();
    let activeDesktopGroup = null;
    let activeCompactGroup = null;
    let isCompactMenuOpen = false;
    let previousMode = getFdHeaderMode(window.innerWidth);

    const isDesktop = () => getFdHeaderMode(window.innerWidth) === 'desktop';
    const isCompact = () => !isDesktop();

    const isReducedMotion = () =>
      window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

    const getDesktopPanelAnimationDuration = () =>
      isReducedMotion() ? 0 : 260;

    const getDesktopPanelReadyDelay = () =>
      isReducedMotion() ? 0 : 100;

    const clearDesktopPanelTimer = (timers, panel) => {
      const timer = timers.get(panel);

      if (timer !== undefined) {
        window.clearTimeout(timer);
        timers.delete(panel);
      }
    };

    const clearDesktopPanelAnimationFrame = (panel) => {
      const frame = desktopPanelAnimationFrames.get(panel);

      if (frame !== undefined) {
        window.cancelAnimationFrame(frame);
        desktopPanelAnimationFrames.delete(panel);
      }
    };

    const getDesktopPanelHeight = (panel) => {
      const content = panel.querySelector('.fd-header-nav__panel-content');

      if (!content) {
        return 0;
      }

      return Math.ceil(
        content.getBoundingClientRect().height
        + panel.offsetHeight
        - panel.clientHeight
      );
    };

    const syncDesktopNavState = () => {
      const hasVisiblePanel = desktopPanels.some((panel) => !panel.hidden);

      desktopNav.classList.toggle(
        'is-open',
        Boolean(activeDesktopGroup) || hasVisiblePanel
      );
      desktopNav.classList.toggle(
        'is-closing',
        !activeDesktopGroup && hasVisiblePanel
      );
    };

    const finalizeDesktopPanelClose = (panel) => {
      clearDesktopPanelTimer(desktopPanelCloseTimers, panel);

      if (panel.classList.contains('is-open')) {
        return;
      }

      panel.hidden = true;
      panel.setAttribute('aria-hidden', 'true');
      panel.inert = true;
      panel.classList.remove('is-ready');
      panel.style.height = '';
      syncDesktopNavState();
    };

    const markDesktopPanelReady = (panel) => {
      clearDesktopPanelTimer(desktopPanelReadyTimers, panel);

      if (panel.classList.contains('is-open')) {
        panel.classList.add('is-ready');
      }
    };

    const queueDesktopPanelReady = (panel) => {
      clearDesktopPanelTimer(desktopPanelReadyTimers, panel);

      const delay = getDesktopPanelReadyDelay();

      if (delay === 0) {
        markDesktopPanelReady(panel);
        return;
      }

      const readyTimer = window.setTimeout(
        () => markDesktopPanelReady(panel),
        delay
      );

      desktopPanelReadyTimers.set(panel, readyTimer);
    };

    const openDesktopPanel = (panel) => {
      clearDesktopPanelTimer(desktopPanelCloseTimers, panel);
      clearDesktopPanelTimer(desktopPanelReadyTimers, panel);
      clearDesktopPanelAnimationFrame(panel);

      panel.hidden = false;
      panel.setAttribute('aria-hidden', 'false');
      panel.inert = false;

      if (panel.classList.contains('is-open')) {
        if (!panel.classList.contains('is-ready')) {
          queueDesktopPanelReady(panel);
        }
        return;
      }

      panel.classList.remove('is-ready');
      panel.style.height = `${panel.getBoundingClientRect().height}px`;

      const revealPanel = () => {
        desktopPanelAnimationFrames.delete(panel);
        panel.classList.add('is-open');
        panel.style.height = `${getDesktopPanelHeight(panel)}px`;

        if (getDesktopPanelAnimationDuration() === 0) {
          markDesktopPanelReady(panel);
          return;
        }

        const readyTimer = window.setTimeout(
          () => markDesktopPanelReady(panel),
          getDesktopPanelAnimationDuration() + getDesktopPanelReadyDelay()
        );

        desktopPanelReadyTimers.set(panel, readyTimer);
      };

      const frame = window.requestAnimationFrame(revealPanel);

      desktopPanelAnimationFrames.set(panel, frame);
    };

    const closeDesktopPanel = (panel, { immediate = false } = {}) => {
      clearDesktopPanelAnimationFrame(panel);
      clearDesktopPanelTimer(desktopPanelReadyTimers, panel);

      panel.style.height = `${panel.getBoundingClientRect().height}px`;

      panel.setAttribute('aria-hidden', 'true');
      panel.inert = true;
      panel.classList.remove('is-open', 'is-ready');

      if (panel.hidden || immediate || getDesktopPanelAnimationDuration() === 0) {
        finalizeDesktopPanelClose(panel);
        return;
      }

      clearDesktopPanelTimer(desktopPanelCloseTimers, panel);

      const collapsePanel = () => {
        desktopPanelAnimationFrames.delete(panel);
        panel.style.height = '0px';
      };

      const frame = window.requestAnimationFrame(collapsePanel);

      desktopPanelAnimationFrames.set(panel, frame);

      const closeTimer = window.setTimeout(
        () => finalizeDesktopPanelClose(panel),
        getDesktopPanelAnimationDuration() + 40
      );

      desktopPanelCloseTimers.set(panel, closeTimer);
    };

    const setDesktopPanelState = (activeGroup, { immediate = false } = {}) => {
      desktopPanels.forEach((panel) => {
        const isActive = panel.dataset.fdDesktopPanel === activeGroup;

        if (isActive) {
          openDesktopPanel(panel);
        } else {
          closeDesktopPanel(panel, { immediate });
        }
      });

      desktopTriggers.forEach((trigger) => {
        const isActive = trigger.dataset.fdDesktopTrigger === activeGroup;

        trigger.setAttribute('aria-expanded', String(isActive));
        trigger.classList.toggle('is-active', isActive);
      });

      syncDesktopNavState();
    };

    const setCompactPanelState = (activeGroup) => {
      compactPanels.forEach((panel) => {
        const isActive = panel.dataset.fdCompactPanel === activeGroup;

        panel.classList.toggle('is-open', isActive);
        panel.setAttribute('aria-hidden', String(!isActive));
        panel.inert = !isActive;
      });

      compactTriggers.forEach((trigger) => {
        const isActive = trigger.dataset.fdCompactTrigger === activeGroup;

        trigger.setAttribute('aria-expanded', String(isActive));
        trigger.classList.toggle('is-active', isActive);
      });
    };

    const setDesktopGroup = (group, { immediate = false } = {}) => {
      activeDesktopGroup = group;
      setDesktopPanelState(group, { immediate });
    };

    const setCompactGroup = (group) => {
      activeCompactGroup = group;
      setCompactPanelState(group);
    };

    const clearDesktopCloseTimeout = () => {
      if (desktopCloseTimeout) {
        window.clearTimeout(desktopCloseTimeout);
        desktopCloseTimeout = null;
      }
    };

    const closeDesktopMenu = ({ immediate = false } = {}) => {
      clearDesktopCloseTimeout();
      setDesktopGroup(null, { immediate });
    };

    const scheduleDesktopMenuClose = () => {
      if (!isDesktop()) {
        return;
      }

      clearDesktopCloseTimeout();
      desktopCloseTimeout = window.setTimeout(closeDesktopMenu, 140);
    };

    const setCompactMenuState = (isOpen, { restoreFocus = false } = {}) => {
      if (!isCompact() && isOpen) {
        return;
      }

      isCompactMenuOpen = isOpen;
      header.classList.toggle('is-open-menu', isOpen);
      compactNav.classList.toggle('is-open', isOpen);
      compactNav.setAttribute('aria-hidden', String(!isOpen));
      menuToggle.classList.toggle('active', isOpen);
      menuToggle.setAttribute('aria-expanded', String(isOpen));
      menuToggle.setAttribute(
        'aria-label',
        isOpen ? 'Close navigation menu' : 'Open navigation menu'
      );
      overlay?.classList.toggle('active', isOpen);
      overlay?.setAttribute('aria-hidden', String(!isOpen));

      if (isOpen) {
        if (typeof scrollLock !== 'undefined') {
          scrollLock.disablePageScroll(compactScroll || compactNav);

          if (compactScroll) {
            scrollLock.addScrollableSelector('[data-fd-compact-scroll]');
          }
        }

        window.lenis?.stop();
        window.requestAnimationFrame(() => {
          getFocusableElements(compactNav)[0]?.focus();
        });
        return;
      }

      setCompactGroup(null);

      if (typeof scrollLock !== 'undefined') {
        scrollLock.enablePageScroll();
      }

      window.lenis?.start();

      if (restoreFocus) {
        menuToggle.focus();
      }
    };

    const closeCompactMenu = (restoreFocus = false) => {
      if (!isCompactMenuOpen) {
        return;
      }

      setCompactMenuState(false, { restoreFocus });
    };

    desktopPanels.forEach((panel) => {
      panel.addEventListener('transitionend', (event) => {
        if (event.target !== panel || event.propertyName !== 'height') {
          return;
        }

        if (panel.classList.contains('is-open')) {
          queueDesktopPanelReady(panel);
          return;
        }

        finalizeDesktopPanelClose(panel);
      });
    });

    setDesktopGroup(null, { immediate: true });
    setCompactGroup(null);

    desktopTriggers.forEach((trigger) => {
      const group = trigger.dataset.fdDesktopTrigger;

      trigger.addEventListener('mouseenter', () => {
        if (!isDesktop()) {
          return;
        }

        clearDesktopCloseTimeout();
        setDesktopGroup(group);
      });

      trigger.addEventListener('focus', () => {
        if (isDesktop()) {
          setDesktopGroup(group);
        }
      });

      trigger.addEventListener('click', () => {
        if (!isDesktop()) {
          return;
        }

        setDesktopGroup(activeDesktopGroup === group ? null : group);
      });
    });

    const closeDesktopMenuOnPlainLink = () => {
      if (isDesktop()) {
        closeDesktopMenu();
      }
    };

    desktopLinks.forEach((link) => {
      link.addEventListener('mouseenter', closeDesktopMenuOnPlainLink);
      link.addEventListener('focus', closeDesktopMenuOnPlainLink);
    });

    desktopNav.addEventListener('mouseenter', clearDesktopCloseTimeout);
    desktopNav.addEventListener('mouseleave', scheduleDesktopMenuClose);

    compactTriggers.forEach((trigger) => {
      trigger.addEventListener('click', () => {
        if (!isCompact()) {
          return;
        }

        setCompactGroup(
          getNextFdAccordionGroup(
            activeCompactGroup,
            trigger.dataset.fdCompactTrigger
          )
        );
      });
    });

    menuToggle.addEventListener('click', () => {
      setCompactMenuState(!isCompactMenuOpen, { restoreFocus: false });
    });

    compactNav.addEventListener('click', (event) => {
      const link = event.target.closest('a[href]');

      if (link && compactNav.contains(link)) {
        closeCompactMenu(false);
      }
    });

    overlay?.addEventListener('click', () => closeCompactMenu(true));

    document.addEventListener('pointerdown', (event) => {
      if (
        isDesktop()
        && activeDesktopGroup
        && !desktopNav.contains(event.target)
      ) {
        closeDesktopMenu();
      }
    });

    document.addEventListener('keydown', (event) => {
      if (isFdHeaderCloseKey(event.key)) {
        if (isCompactMenuOpen) {
          event.preventDefault();
          closeCompactMenu(true);
          return;
        }

        if (activeDesktopGroup) {
          event.preventDefault();
          closeDesktopMenu();
        }
      }

      if (!isCompactMenuOpen || event.key !== 'Tab') {
        return;
      }

      const focusableElements = getFocusableElements(compactNav);
      const firstElement = focusableElements[0];
      const lastElement = focusableElements.at(-1);

      if (!firstElement || !lastElement) {
        return;
      }

      if (event.shiftKey && document.activeElement === firstElement) {
        event.preventDefault();
        lastElement.focus();
      } else if (!event.shiftKey && document.activeElement === lastElement) {
        event.preventDefault();
        firstElement.focus();
      }
    });

    const syncResponsiveState = () => {
      const currentMode = getFdHeaderMode(window.innerWidth);

      if (currentMode === previousMode) {
        if (isDesktop()) {
          desktopPanels.forEach((panel) => {
            if (panel.classList.contains('is-open')) {
              panel.style.height = `${getDesktopPanelHeight(panel)}px`;
            }
          });
        }

        return;
      }

      previousMode = currentMode;
      closeDesktopMenu({ immediate: true });
      closeCompactMenu(false);
      compactNav.setAttribute('aria-hidden', 'true');
    };

    window.addEventListener('resize', syncResponsiveState, { passive: true });
    window.addEventListener('orientationchange', syncResponsiveState, {
      passive: true,
    });

    let previousScrollPosition = window.scrollY;
    let isStickyUpdateScheduled = false;

    const updateStickyHeader = () => {
      isStickyUpdateScheduled = false;

      if (isCompactMenuOpen) {
        return;
      }

      const currentScrollPosition = window.scrollY;
      const headerHeight = header.scrollHeight;

      if (currentScrollPosition > 100) {
        if (previousScrollPosition > currentScrollPosition) {
          header.style.top = '0';
          header.classList.add('scrolled');
        } else {
          header.style.top = `-${headerHeight + 3}px`;
        }
      } else {
        header.style.top = '0';
        header.classList.toggle('scrolled', currentScrollPosition > 0);
      }

      previousScrollPosition = currentScrollPosition;
    };

    window.addEventListener(
      'scroll',
      () => {
        if (isStickyUpdateScheduled) {
          return;
        }

        isStickyUpdateScheduled = true;
        window.requestAnimationFrame(updateStickyHeader);
      },
      { passive: true }
    );

    updateStickyHeader();
  });
};

if (typeof document !== 'undefined') {
  initFdHeader();
}
